import { ButtonHTMLAttributes, forwardRef } from 'react'

import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'

import { FocusToggleIcon, UnfocusToggleIcon } from '@/src/assets/icons'
import { LayoutContext, useMaybeTrackRefContext } from '@/src/context'
import { useFocusToggle } from '@/src/hooks'

/** @public */
export interface FocusToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  trackRef?: TrackReferenceOrPlaceholder
  isPiP?: boolean
}

/**
 * The `FocusToggle` puts the `ParticipantTile` in focus or removes it from focus.
 * @remarks
 * This component needs to live inside `LayoutContext` to work properly.
 *
 * @example
 * ```tsx
 * <ParticipantTile>
 *   <FocusToggle />
 * </ParticipantTile>
 * ```
 * @public
 */
export const FocusToggle = /* @__PURE__ */ forwardRef<HTMLButtonElement, FocusToggleProps>(
  function FocusToggle({ trackRef, isPiP = false, ...props }: FocusToggleProps, ref) {
    const trackRefFromContext = useMaybeTrackRefContext()

    const { mergedProps, inFocus } = useFocusToggle({
      trackRef: trackRef ?? trackRefFromContext,
      props,
    })

    return (
      <LayoutContext.Consumer>
        {(layoutContext) =>
          layoutContext !== undefined && (
            <button
              ref={ref}
              {...mergedProps}
              aria-label={inFocus ? 'Unfocus participant tile' : 'Focus participant tile'}
            >
              {props.children ? (
                props.children
              ) : inFocus ? (
                <UnfocusToggleIcon />
              ) : (
                <FocusToggleIcon />
              )}
            </button>
          )
        }
      </LayoutContext.Consumer>
    )
  },
)
